<script>
import { mapGetters } from 'vuex';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import {
  getSortedAgentsByAvailability,
  getAgentsByUpdatedPresence,
} from 'dashboard/helper/agentHelper.js';
import MenuItem from './menuItem.vue';
import MenuItemWithSubmenu from './menuItemWithSubmenu.vue';
import wootConstants from 'dashboard/constants/globals';
import AgentLoadingPlaceholder from './agentLoadingPlaceholder.vue';
import PipelineCardsAPI from 'dashboard/api/pipelineCards';

const MENU = {
  MARK_AS_READ: 'mark-as-read',
  MARK_AS_UNREAD: 'mark-as-unread',
  STATUS: 'status',
  SNOOZE: 'snooze',
  AGENT: 'agent',
  TEAM: 'team',
  LABEL: 'label',
  PIPELINE: 'pipeline',
  DELETE: 'delete',
  OPEN_NEW_TAB: 'open-new-tab',
  COPY_LINK: 'copy-link',
};

export default {
  components: {
    MenuItem,
    MenuItemWithSubmenu,
    AgentLoadingPlaceholder,
  },
  props: {
    chatId: {
      type: Number,
      default: null,
    },
    status: {
      type: String,
      default: '',
    },
    hasUnreadMessages: {
      type: Boolean,
      default: false,
    },
    inboxId: {
      type: Number,
      default: null,
    },
    conversationLabels: {
      type: Array,
      default: () => [],
    },
    conversationUrl: {
      type: String,
      default: '',
    },
    conversationDisplayId: {
      type: Number,
      default: null,
    },
    contactId: {
      type: Number,
      default: null,
    },
    contactName: {
      type: String,
      default: '',
    },
    allowedOptions: {
      type: Array,
      default: () => [],
    },
  },
  emits: [
    'updateConversation',
    'markAsUnread',
    'markAsRead',
    'assignAgent',
    'assignTeam',
    'assignLabel',
    'addToPipeline',
    'removeLabel',
    'deleteConversation',
    'close',
  ],
  setup() {
    const { isAdmin } = useAdmin();
    return {
      isAdmin,
    };
  },
  data() {
    return {
      MENU,
      STATUS_TYPE: wootConstants.STATUS_TYPE,
      readOption: {
        label: this.$t('CONVERSATION.CARD_CONTEXT_MENU.MARK_AS_READ'),
        icon: 'mail',
      },
      unreadOption: {
        label: this.$t('CONVERSATION.CARD_CONTEXT_MENU.MARK_AS_UNREAD'),
        icon: 'mail-unread',
      },
      statusMenuConfig: [
        {
          key: wootConstants.STATUS_TYPE.RESOLVED,
          label: this.$t('CONVERSATION.CARD_CONTEXT_MENU.RESOLVED'),
          icon: 'checkmark',
        },
        {
          key: wootConstants.STATUS_TYPE.OPEN,
          label: this.$t('CONVERSATION.CARD_CONTEXT_MENU.REOPEN'),
          icon: 'arrow-redo',
        },
      ],
      snoozeOption: {
        key: wootConstants.STATUS_TYPE.SNOOZED,
        label: this.$t('CONVERSATION.CARD_CONTEXT_MENU.SNOOZE.TITLE'),
        icon: 'snooze',
      },
      labelMenuConfig: {
        key: MENU.LABEL,
        icon: 'tag',
        label: this.$t('CONVERSATION.CARD_CONTEXT_MENU.ASSIGN_LABEL'),
      },
      agentMenuConfig: {
        key: MENU.AGENT,
        icon: 'person-add',
        label: this.$t('CONVERSATION.CARD_CONTEXT_MENU.ASSIGN_AGENT'),
      },
      teamMenuConfig: {
        key: MENU.TEAM,
        icon: 'people-team-add',
        label: this.$t('CONVERSATION.CARD_CONTEXT_MENU.ASSIGN_TEAM'),
      },
      deleteOption: {
        key: MENU.DELETE,
        icon: 'delete',
        label: this.$t('CONVERSATION.CARD_CONTEXT_MENU.DELETE'),
      },
      openInNewTabOption: {
        key: MENU.OPEN_NEW_TAB,
        icon: 'open',
        label: this.$t('CONVERSATION.CARD_CONTEXT_MENU.OPEN_IN_NEW_TAB'),
      },
      copyLinkOption: {
        key: MENU.COPY_LINK,
        icon: 'copy',
        label: this.$t('CONVERSATION.CARD_CONTEXT_MENU.COPY_LINK'),
      },
      pipelineMenuConfig: {
        key: MENU.PIPELINE,
        icon: 'list',
        label: this.$t('CONVERSATION.CARD_CONTEXT_MENU.ADD_TO_PIPELINE'),
      },
      existingPipelineCards: [],
      isLoadingPipelines: false,
    };
  },
  computed: {
    ...mapGetters({
      labels: 'labels/getLabels',
      teams: 'teams/getTeams',
      pipelines: 'pipelines/getPipelines',
      pipelinesUiFlags: 'pipelines/getUIFlags',
      assignableAgentsUiFlags: 'inboxAssignableAgents/getUIFlags',
      currentUser: 'getCurrentUser',
      currentAccountId: 'getCurrentAccountId',
    }),
    existingCardForPipeline() {
      // Map pipeline_id -> existing card for quick lookup
      const map = {};
      this.existingPipelineCards.forEach(card => {
        map[String(card.pipeline_id)] = card;
      });
      return map;
    },
    filteredAgentOnAvailability() {
      const agents = this.$store.getters[
        'inboxAssignableAgents/getAssignableAgents'
      ](this.inboxId);
      const agentsByUpdatedPresence = getAgentsByUpdatedPresence(
        agents,
        this.currentUser,
        this.currentAccountId
      );
      const filteredAgents = getSortedAgentsByAvailability(
        agentsByUpdatedPresence
      );
      return filteredAgents;
    },
    assignableAgents() {
      return [
        {
          confirmed: true,
          name: 'None',
          id: null,
          role: 'agent',
          account_id: 0,
          email: 'None',
        },
        ...this.filteredAgentOnAvailability,
      ];
    },
    showSnooze() {
      // Don't show snooze if the conversation is already snoozed/resolved/pending
      return this.status === wootConstants.STATUS_TYPE.OPEN;
    },
  },
  mounted() {
    // A lista de agentes do submenu. Se falhar, a lista fica vazia e o item
    // "Atribuir agente" aparece INERTE (cinza, sem abrir) — não mostra uma
    // lista falsa. O `catch` só impede a rejeição solta no console.
    this.$store
      .dispatch('inboxAssignableAgents/fetch', [this.inboxId])
      .catch(() => {});
    this.fetchPipelinesAndCards();
  },
  methods: {
    isAllowed(keys) {
      if (!this.allowedOptions.length) return true;
      return keys.some(key => this.allowedOptions.includes(key));
    },
    toggleStatus(status, snoozedUntil) {
      this.$emit('updateConversation', status, snoozedUntil);
    },
    async snoozeConversation() {
      await this.$store.dispatch('setContextMenuChatId', this.chatId);
      const ninja = document.querySelector('ninja-keys');
      ninja.open({ parent: 'snooze_conversation' });
    },
    deleteConversation() {
      this.$emit('deleteConversation', this.chatId);
    },
    openInNewTab() {
      if (!this.conversationUrl) return;

      const url = `${window.chatwootConfig.hostURL}${this.conversationUrl}`;
      window.open(url, '_blank', 'noopener,noreferrer');
      this.$emit('close');
    },
    async copyConversationLink() {
      if (!this.conversationUrl) return;
      try {
        const url = `${window.chatwootConfig.hostURL}${this.conversationUrl}`;
        await copyTextToClipboard(url);
        useAlert(this.$t('CONVERSATION.CARD_CONTEXT_MENU.COPY_LINK_SUCCESS'));
        this.$emit('close');
      } catch (error) {
        // error
      }
    },
    show(key) {
      // If the conversation status is same as the action, then don't display the option
      // i.e.: Don't show an option to resolve if the conversation is already resolved.
      return this.status !== key;
    },
    generateMenuLabelConfig(option, type = 'text') {
      return {
        key: option.id,
        ...(type === 'icon' && { icon: option.icon }),
        ...(type === 'label' && { color: option.color }),
        ...(type === 'agent' && { thumbnail: option.thumbnail }),
        ...(type === 'agent' && { status: option.availability_status }),
        ...(type === 'text' && { label: option.label }),
        ...(type === 'label' && { label: option.title }),
        ...(type === 'agent' && { label: option.name }),
        ...(type === 'team' && { label: option.name }),
        ...(type === 'pipeline' && { label: option.name, icon: 'list' }),
      };
    },
    async fetchPipelinesAndCards() {
      this.isLoadingPipelines = true;
      try {
        // Fetch pipelines if not already loaded
        if (!this.pipelines.length) {
          await this.$store.dispatch('pipelines/fetch');
        }

        // Fetch existing cards for this conversation
        if (this.conversationDisplayId) {
          const response = await PipelineCardsAPI.getCardByConversationId(
            this.conversationDisplayId
          );
          this.existingPipelineCards = response.data || [];
        }
      } catch (error) {
        // Silent fail - menu still works without pipeline info
      } finally {
        this.isLoadingPipelines = false;
      }
    },
    getPipelineStages(pipeline) {
      if (!pipeline.stages) return [];

      return Object.entries(pipeline.stages)
        .map(([key, stage]) => ({
          key,
          name: stage.name || key,
          position: stage.position || 0,
          isEntryStage: stage.is_entry_stage || false,
        }))
        .sort((a, b) => a.position - b.position);
    },
    buildCardData(pipeline, stageKey) {
      return {
        pipeline_id: pipeline.id,
        pipeline_stage: stageKey,
        contact_id: this.contactId,
        conversation_display_id: this.conversationDisplayId,
        title:
          this.contactName ||
          (this.conversationDisplayId
            ? `Lead #${this.conversationDisplayId}`
            : 'Lead'),
        item_details: {
          source: 'context_menu',
          conversation_display_id: this.conversationDisplayId,
        },
      };
    },
    getExistingCard(pipeline) {
      return this.existingCardForPipeline[String(pipeline.id)] || null;
    },
    isCurrentStage(pipeline, stageKey) {
      const card = this.getExistingCard(pipeline);
      return card && card.pipeline_stage === stageKey;
    },
    async handleStageClick(pipeline, stageKey) {
      const existingCard = this.getExistingCard(pipeline);
      if (existingCard) {
        await this.moveCardToStage(existingCard, pipeline, stageKey);
      } else {
        await this.addToPipelineWithStage(pipeline, stageKey);
      }
    },
    async addToPipelineWithStage(pipeline, stageKey) {
      try {
        const cardData = this.buildCardData(pipeline, stageKey);
        await PipelineCardsAPI.createCard(cardData);

        const stageName = pipeline.stages[stageKey]?.name || stageKey;
        useAlert(
          this.$t(
            'CONVERSATION.CARD_CONTEXT_MENU.API.PIPELINE_ASSIGNMENT.SUCCESFUL',
            {
              pipelineName: `${pipeline.name} - ${stageName}`,
            }
          )
        );

        this.existingPipelineCards.push({
          pipeline_id: pipeline.id,
          pipeline_stage: stageKey,
        });

        this.$emit('addToPipeline', pipeline);
        this.$emit('close');
      } catch (error) {
        useAlert(
          this.$t(
            'CONVERSATION.CARD_CONTEXT_MENU.API.PIPELINE_ASSIGNMENT.FAILED'
          )
        );
      }
    },
    async moveCardToStage(card, pipeline, stageKey) {
      try {
        await PipelineCardsAPI.updateCard(card.id, {
          pipeline_stage: stageKey,
        });

        const stageName = pipeline.stages[stageKey]?.name || stageKey;
        useAlert(
          this.$t(
            'CONVERSATION.CARD_CONTEXT_MENU.API.PIPELINE_ASSIGNMENT.SUCCESFUL',
            {
              pipelineName: `${pipeline.name} - ${stageName}`,
            }
          )
        );

        // Update local state
        const idx = this.existingPipelineCards.findIndex(
          c => String(c.pipeline_id) === String(pipeline.id)
        );
        if (idx !== -1) {
          this.existingPipelineCards[idx].pipeline_stage = stageKey;
        }

        this.$emit('addToPipeline', pipeline);
        this.$emit('close');
      } catch (error) {
        useAlert(
          this.$t(
            'CONVERSATION.CARD_CONTEXT_MENU.API.PIPELINE_ASSIGNMENT.FAILED'
          )
        );
      }
    },
  },
};
</script>

<template>
  <div class="advup-menu ctx-menu" role="menu">
    <template v-if="isAllowed([MENU.MARK_AS_READ, MENU.MARK_AS_UNREAD])">
      <MenuItem
        v-if="!hasUnreadMessages"
        :option="unreadOption"
        variant="icon"
        @click.stop="$emit('markAsUnread')"
      />
      <MenuItem
        v-else
        :option="readOption"
        variant="icon"
        @click.stop="$emit('markAsRead')"
      />
      <hr class="advup-menu__filete" />
    </template>
    <template v-if="isAllowed([MENU.STATUS, MENU.SNOOZE])">
      <template v-for="option in statusMenuConfig">
        <MenuItem
          v-if="show(option.key) && isAllowed([MENU.STATUS])"
          :key="option.key"
          :option="option"
          variant="icon"
          @click.stop="toggleStatus(option.key, null)"
        />
      </template>
      <!-- advup: item "Adiar" (snooze) removido do menu por pedido do Victor -->
      <hr class="advup-menu__filete" />
    </template>
    <template v-if="isAllowed([MENU.LABEL, MENU.AGENT, MENU.TEAM])">
      <MenuItemWithSubmenu
        v-if="isAllowed([MENU.LABEL])"
        :option="labelMenuConfig"
        :sub-menu-available="!!labels.length"
      >
        <MenuItem
          v-for="label in labels"
          :key="label.id"
          :option="generateMenuLabelConfig(label, 'label')"
          :variant="
            conversationLabels.includes(label.title)
              ? 'label-assigned'
              : 'label'
          "
          @click.stop="
            conversationLabels.includes(label.title)
              ? $emit('removeLabel', label)
              : $emit('assignLabel', label)
          "
        />
      </MenuItemWithSubmenu>
      <MenuItemWithSubmenu
        v-if="isAllowed([MENU.AGENT])"
        :option="agentMenuConfig"
        :sub-menu-available="!!assignableAgents.length"
      >
        <AgentLoadingPlaceholder v-if="assignableAgentsUiFlags.isFetching" />
        <template v-else>
          <MenuItem
            v-for="agent in assignableAgents"
            :key="agent.id"
            :option="generateMenuLabelConfig(agent, 'agent')"
            variant="agent"
            @click.stop="$emit('assignAgent', agent)"
          />
        </template>
      </MenuItemWithSubmenu>
      <MenuItemWithSubmenu
        v-if="isAllowed([MENU.TEAM])"
        :option="teamMenuConfig"
        :sub-menu-available="!!teams.length"
      >
        <MenuItem
          v-for="team in teams"
          :key="team.id"
          :option="generateMenuLabelConfig(team, 'team')"
          @click.stop="$emit('assignTeam', team)"
        />
      </MenuItemWithSubmenu>
      <MenuItemWithSubmenu
        v-if="isAllowed([MENU.PIPELINE])"
        :option="pipelineMenuConfig"
        :sub-menu-available="pipelines.length > 0"
      >
        <div
          v-if="isLoadingPipelines || pipelinesUiFlags.isFetching"
          class="px-3 py-2 text-xs text-n-slate-11"
        >
          {{ $t('CONVERSATION.CARD_CONTEXT_MENU.PIPELINES_LOADING') }}
        </div>
        <template v-else-if="pipelines.length">
          <template v-for="pipeline in pipelines" :key="pipeline.id">
            <div
              v-if="pipelines.length > 1"
              class="px-3 py-1 text-xs font-medium text-n-slate-11 uppercase tracking-wide"
            >
              {{ pipeline.name }}
            </div>
            <MenuItem
              v-for="stage in getPipelineStages(pipeline)"
              :key="`${pipeline.id}-${stage.key}`"
              :option="{
                label: isCurrentStage(pipeline, stage.key)
                  ? `✓ ${stage.name}`
                  : stage.name,
                key: stage.key,
              }"
              :class="{
                'opacity-50 cursor-not-allowed': isCurrentStage(
                  pipeline,
                  stage.key
                ),
              }"
              @click.stop="
                !isCurrentStage(pipeline, stage.key) &&
                  handleStageClick(pipeline, stage.key)
              "
            />
            <hr
              v-if="pipelines.length > 1"
              class="m-1 border-b border-n-weak"
            />
          </template>
        </template>
        <div v-else class="px-3 py-2 text-xs text-n-slate-11">
          {{ $t('CONVERSATION.CARD_CONTEXT_MENU.NO_PIPELINES') }}
        </div>
      </MenuItemWithSubmenu>
      <hr class="advup-menu__filete" />
    </template>
    <!-- advup: "Abrir em nova aba" e "Copiar link da conversa" removidos por pedido do Victor -->
    <template v-if="isAdmin && isAllowed([MENU.DELETE])">
      <MenuItem
        :option="deleteOption"
        variant="icon"
        perigo
        @click.stop="deleteConversation"
      />
    </template>
  </div>
</template>

<style scoped>
/* Menu de contexto do cartão da conversa (botão direito). A caixa é a peça do
   produto (`.advup-menu`) — superfície sólida; era um véu de 50% com desfoque
   de 100px, e o texto da lista vazava através das opções. Aqui só mora a
   chegada: cresce a partir do canto onde o clique aconteceu. */
.ctx-menu {
  min-width: 220px;
  transform-origin: top left;
  animation: ctx-chega var(--mov-2) var(--mov-curva-pulo) both;
}

@keyframes ctx-chega {
  from {
    opacity: 0;
    scale: 0.96;
    translate: 0 -3px;
  }
}

.advup-menu__filete {
  border: 0;
}

@media (prefers-reduced-motion: reduce) {
  .ctx-menu {
    animation: none;
  }
}
</style>
